import _ from 'lodash';
import { apolloQueryWrapper } from '@/shared/api';
import HOLTER_BEAT_EVENTS_COUNT_QUERY from '@/entities/beat/api/queries/holterBeatEventsCount';
import type { ReviewedCount } from '@/shared/api';

export type HolterBeatEventsCount = {
  isSuccess: boolean;
  message: string;
  veCouplet: ReviewedCount;
  veBigeminy: ReviewedCount;
  veTrigeminal: ReviewedCount;
  veRun: ReviewedCount;
  veQuadrigeminy: ReviewedCount;
  sveCouplet: ReviewedCount;
  sveBigeminy: ReviewedCount;
  sveTrigeminal: ReviewedCount;
  sveRun: ReviewedCount;
  sveQuadrigeminy: ReviewedCount;
};

// TODO(ts): typed as the HolterEventsFilter GraphQL input once its server-side shape is documented
/**
 * Loads the per-type Holter beat event counts and review progress for a study. Runs the holterBeatEventsCount query through apolloQueryWrapper and, on success, clones the payload, strips isSuccess and message, and rewrites null buckets as a count of zero at one hundred percent reviewed, otherwise throwing the server message.
 *
 * @param filter - GraphQL filter narrowing the counts to a study and time range.
 * @param isUsingCache - When true, lets the wrapper serve a cached result.
 * @param key - Cancellation key identifying this request.
 * @param needCancel - Whether an in-flight request under the same key is cancelled first.
 * @returns A promise resolving to a map of beat event type to its count and reviewed percentage.
 */
const fetchHolterBeatEventsCount = async (
  filter: any,
  isUsingCache?: boolean,
  key?: string | null,
  needCancel = true,
) => {
  const result = await apolloQueryWrapper<{ holterBeatEventsCount: HolterBeatEventsCount }>({
    options: {
      query: HOLTER_BEAT_EVENTS_COUNT_QUERY,
      variables: {
        filter,
      },
    },
    isUsingCache,
    key,
    needCancel,
  });
  const { data } = result;
  const { holterBeatEventsCount } = data;
  if (holterBeatEventsCount?.isSuccess) {
    // cloneDeep to mutate read only object
    const objectClone: Record<string, any> = _.cloneDeep(holterBeatEventsCount);
    delete objectClone.isSuccess;
    delete objectClone.message;
    _.forEach(objectClone, (value, key) => {
      if (value === null) {
        objectClone[key] = {
          count: 0,
          reviewed: 100,
        };
      }
    });
    return objectClone;
  }
  throw new Error(holterBeatEventsCount.message);
};

export default fetchHolterBeatEventsCount;
